import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { ManualLabelSelectionIndicatorComponent } from 'app/shared/components/manual-label-selection-indicator';

export interface SimpleLabelData {
  label: string;
  quantity: number;
  parallelQuantity?: number;
}

export interface SimpleBatchData {
  bacth: string;
  quantity: number;
  parallelQuantity?: number;
  labels?: SimpleLabelData[];
  expanded?: boolean;
}

export interface SimpleBatchesDialogData {
  title: string;
  materialCode?: string;
  materialName?: string;
  batches: SimpleBatchData[];
  showUMP?: boolean;
  showLabels?: boolean;
  documentSerial?: string;
  baseQuantity?: number;
  parallelQuantity?: number;
  /**
   * FS-240: whether the line's batches/labels were hand-picked at delivery
   * (`OrderDetailAttributes.deliveryLabelsManuallySelect`). Callers that show a
   * single detail line pass it; aggregated views leave it out.
   */
  manuallySelected?: boolean;
}

export interface SimpleBatchesDialogResponse {
  action: 'close' | 'cancel';
  data?: SimpleBatchData[];
}

@Component({
  selector: 'app-simple-batches-viewer',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatTableModule,
    MatButtonModule,
    MatIcon,
    MatTooltipModule,
    ManualLabelSelectionIndicatorComponent
  ],
  templateUrl: './simple-batches-viewer.component.html',
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({
        height: '0',
        minHeight: '0',
        padding: '0',
        margin: '0',
        visibility: 'hidden',
        opacity: '0',
        border: 'none'
      })),
      state('expanded', style({
        height: '*',
        visibility: 'visible',
        opacity: '1'
      })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ]
})
export class SimpleBatchesViewerComponent implements OnInit {

  dataSourceBatches = new MatTableDataSource<SimpleBatchData>([]);
  displayedColumnsBatches: string[] = ['expand', 'batch', 'umb'];
  displayedColumnsDetail: string[] = ['batch', 'umb'];

  constructor(
    public dialogRef: MatDialogRef<SimpleBatchesViewerComponent, SimpleBatchesDialogResponse>,
    @Inject(MAT_DIALOG_DATA) public data: SimpleBatchesDialogData
  ) {}

  ngOnInit(): void {
    this.dataSourceBatches.data = this.data.batches || [];
    this.setDisplayedColumns();
  }

  private setDisplayedColumns(): void {
    const baseColumns = ['expand', 'batch', 'umb'];
    const detailColumns = ['batch', 'umb'];
    
    if (this.data.showUMP) {
      baseColumns.push('ump');
      detailColumns.push('ump');
    }
    
    this.displayedColumnsBatches = baseColumns;
    this.displayedColumnsDetail = detailColumns;
  }

  toggleExpand(batch: SimpleBatchData): void {
    if (batch.labels && batch.labels.length > 0) {
      batch.expanded = !batch.expanded;
    }
  }

  onClose(): void {
    const response: SimpleBatchesDialogResponse = {
      action: 'close',
      data: this.dataSourceBatches.data
    };
    this.dialogRef.close(response);
  }

  trackBy(index: number, item: SimpleBatchData): number {
    return index;
  }

  trackByLabelFn = (index: number, item: SimpleLabelData): string | number => item.label || index;

  hasNestedContent = (index: number, item: SimpleBatchData): boolean => 
    !!(item.labels && item.labels.length > 0 && this.data.showLabels);
}
